// useSseWithTokenRefresh — Server-Sent Events with token-refresh-aware manual
// reconnect.
//
// The browser's native EventSource can't set an Authorization header, so the
// access token rides along as a `?token=` query param. Native auto-reconnect
// reuses the original URL — and therefore the original token — so once that
// token expires every reconnect 401s and the stream stays dead until a full
// remount. This helper takes over reconnection: on every (re)connect it
// re-reads a fresh token (proactively refreshing an expiring one via
// ensureFreshToken) before opening a new EventSource, with exponential
// backoff, and gives up cleanly once the session can no longer be refreshed.

import { useEffect, useRef, useState } from 'react';

import { apiClient, ensureFreshToken } from '../api/client';

// --- Shared URL helper (consolidated from the two hooks) ---------------------

/** Resolve the SSE base URL from the axios instance so feeds honour the same
 *  VITE_API_URL / proxy base the rest of the app uses. Defensive about a
 *  mocked `raw` (test doubles often omit `defaults`). */
export function sseBaseUrl(): string {
  const raw = apiClient.raw as unknown as { defaults?: { baseURL?: string } };
  const base = raw.defaults?.baseURL ?? '/api/v1';
  return base.replace(/\/+$/, '');
}

// --- Core reconnect engine (framework-agnostic) ------------------------------

export interface SseHandlers {
  [eventName: string]: (ev: MessageEvent) => void;
}

export interface SseOptions {
  realm?: 'tenant' | 'super';
  baseBackoffMs?: number; // default 1_000
  maxBackoffMs?: number; // default 30_000
  onOpen?: () => void;
  onError?: (err: Event) => void;
  /** Fires on the server-sent `connected` event (not the native `open`). */
  onConnected?: () => void;
}

/**
 * Opens an SSE connection with token-refresh-aware manual reconnect.
 *
 * - On error: closes, waits (exponential backoff + jitter), refreshes the
 *   token, reopens.
 * - Resets the backoff counter when the server confirms with `connected`.
 * - Stops reconnecting if ensureFreshToken returns null (session dead).
 *
 * Returns a `{ close }` handle for cleanup.
 */
export function createSseConnection(
  buildUrl: (token: string) => string,
  handlers: SseHandlers,
  options: SseOptions = {},
): { close: () => void } {
  const {
    realm = 'tenant',
    baseBackoffMs = 1_000,
    maxBackoffMs = 30_000,
    onOpen,
    onError,
    onConnected,
  } = options;

  let es: EventSource | null = null;
  let attempt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;

  function teardownEventSource(): void {
    if (es) {
      es.close();
      es = null;
    }
  }

  async function connect(): Promise<void> {
    if (closed) return;
    if (typeof EventSource === 'undefined') return;

    // First open (attempt 0) uses the current token; every reconnect after an
    // error forces a refresh, since the server can 401 a token the client
    // still considers valid (clock skew, revoked jti, secret rotation) and a
    // non-forced refresh would hand back the same rejected token forever.
    const token = await ensureFreshToken(realm, attempt > 0);
    // The session may have been torn down while the refresh was in flight.
    if (closed) return;
    if (!token) {
      // Can't get a usable token — session dead. Stop reconnecting; the
      // axios logout flow handles routing the user out.
      return;
    }

    teardownEventSource();
    if (closed) return;

    let current: EventSource;
    try {
      current = new EventSource(buildUrl(token));
    } catch {
      return; // Environment without EventSource support.
    }
    es = current;

    current.addEventListener('open', () => {
      onOpen?.();
    });

    current.addEventListener('connected', (ev: Event) => {
      attempt = 0; // server confirmed — reset backoff
      onConnected?.();
      handlers['connected']?.(ev as MessageEvent);
    });

    for (const [name, handler] of Object.entries(handlers)) {
      if (name === 'connected') continue; // registered above
      current.addEventListener(name, handler as EventListener);
    }

    current.addEventListener('error', (ev: Event) => {
      onError?.(ev);
      // Take over from native reconnect: close and schedule our own retry
      // with a freshly-refreshed token.
      teardownEventSource();
      if (closed) return;
      const jitter = Math.random() * 200;
      const backoff =
        Math.min(baseBackoffMs * Math.pow(2, attempt), maxBackoffMs) + jitter;
      attempt += 1;
      timer = setTimeout(() => {
        void connect();
      }, backoff);
    });
  }

  void connect();

  return {
    close(): void {
      closed = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      teardownEventSource();
    },
  };
}

// --- React hook wrapper ------------------------------------------------------

export interface UseSseOptions extends SseOptions {
  enabled?: boolean;
}

export function useSseWithTokenRefresh(
  buildUrl: (token: string) => string,
  handlers: SseHandlers,
  options: UseSseOptions = {},
): { connected: boolean } {
  const { enabled = true, ...sseOptions } = options;
  const [connected, setConnected] = useState(false);

  // Keep moving parts in refs so changing identities don't reconnect.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const buildUrlRef = useRef(buildUrl);
  buildUrlRef.current = buildUrl;
  const optionsRef = useRef(sseOptions);
  optionsRef.current = sseOptions;

  useEffect(() => {
    if (!enabled) {
      setConnected(false);
      return;
    }

    // Capture the handler key set once (names are static per call site) and
    // wrap each so the connection always invokes the latest callback.
    const wrapped: SseHandlers = {};
    for (const name of Object.keys(handlersRef.current)) {
      wrapped[name] = (ev) => handlersRef.current[name]?.(ev);
    }

    const conn = createSseConnection(
      (token) => buildUrlRef.current(token),
      wrapped,
      {
        ...optionsRef.current,
        onConnected: () => {
          setConnected(true);
          optionsRef.current.onConnected?.();
        },
        onError: (ev) => {
          setConnected(false);
          optionsRef.current.onError?.(ev);
        },
      },
    );

    return () => {
      conn.close();
      setConnected(false);
    };
  }, [enabled]);

  return { connected };
}
